import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';
import { ReactComponent as DashboardIcon } from '../icons/Home.svg';
import { ReactComponent as UserIcon } from '../icons/user.svg';
import { ReactComponent as SignatureIcon } from '../icons/signatures.svg';
import { ReactComponent as GroupIcon } from '../icons/groups.svg';
import { ReactComponent as AlertIcon } from '../icons/alert.svg';
import { ReactComponent as SettingsIcon } from '../icons/settings.svg';
import { ReactComponent as SidebarIcon } from '../icons/sidebar.svg';
import { ReactComponent as LogoutIcon } from '../icons/logout.svg';
import { ReactComponent as LicenseIcon } from '../icons/license.svg';
import { ReactComponent as ActivityLogIcon } from '../icons/activity-log.svg';
import { ReactComponent as LegalIcon } from '../icons/legal.svg';
import { ReactComponent as CampaignIcon } from '../icons/campaign.svg';
import { ReactComponent as SurveyIcon } from '../icons/survey.svg';
import { ReactComponent as DeliveryIcon } from '../icons/delivery.svg';
import { ReactComponent as HelpIcon } from '../icons/help.svg';
import { ReactComponent as ImageIcon } from '../icons/image.svg';
import useBrandLogo from '../hooks/useBrandLogo';
import { getInitials } from '../utils/initials';
import useIsMobile from '../hooks/useIsMobile';
import { useT } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher';

const icons = {
  dashboard: <DashboardIcon className="sidebar-icon" />,
  adminPanel: <DashboardIcon className="sidebar-icon" />,
  staff: <UserIcon className="sidebar-icon" />,
  users: <UserIcon className="sidebar-icon" />,
  signatures: <SignatureIcon className="sidebar-icon" />,
  mySignature: <SignatureIcon className="sidebar-icon" />,
  orgPanel: <GroupIcon className="sidebar-icon" />,
  superAdminPanel: <DashboardIcon className="sidebar-icon" />,
  groups: <GroupIcon className="sidebar-icon" />,
  organizations: <GroupIcon className="sidebar-icon" />,
  licenses: <LicenseIcon className="sidebar-icon" />,
  auditLog: <ActivityLogIcon className="sidebar-icon" />,
  demoRequests: <CampaignIcon className="sidebar-icon" />,
  disclaimers: <LegalIcon className="sidebar-icon" />,
  campaigns: <CampaignIcon className="sidebar-icon" />,
  surveys: <SurveyIcon className="sidebar-icon" />,
  deliveryLog: <DeliveryIcon className="sidebar-icon" />,
  brandAssets: <ImageIcon className="sidebar-icon" />,
  alerts: <AlertIcon className="sidebar-icon" />,
  help: <HelpIcon className="sidebar-icon" />,
  settings: <SettingsIcon className="sidebar-icon" />,
  logout: <LogoutIcon className="sidebar-icon" />
};

const messages = {
  dashboard: { tr: 'Anasayfa', en: 'Dashboard' },
  adminPanel: { tr: 'Admin Panel', en: 'Admin Panel' },
  staff: { tr: 'Personeller', en: 'People' },
  users: { tr: 'Kullanıcılar', en: 'Users' },
  signatures: { tr: 'İmzalar', en: 'Signatures' },
  mySignature: { tr: 'İmzam', en: 'My Signature' },
  orgPanel: { tr: 'Organizasyon Paneli', en: 'Organization Panel' },
  superAdminPanel: { tr: 'Süper Admin Paneli', en: 'Super Admin Panel' },
  groups: { tr: 'Gruplar', en: 'Groups' },
  organizations: { tr: 'Organizasyonlar', en: 'Organizations' },
  licenses: { tr: 'Lisanslar', en: 'Licenses' },
  auditLog: { tr: 'Aktivite Günlüğü', en: 'Activity Log' },
  demoRequests: { tr: 'Demo Talepleri', en: 'Demo Requests' },
  disclaimers: { tr: 'Yasal Uyarılar', en: 'Disclaimers' },
  campaigns: { tr: 'Kampanyalar', en: 'Campaigns' },
  surveys: { tr: 'Memnuniyet Anketi', en: 'Satisfaction Survey' },
  deliveryLog: { tr: 'Teslimat Günlüğü', en: 'Delivery Log' },
  brandAssets: { tr: 'Marka Varlıkları', en: 'Brand Assets' },
  alerts: { tr: 'Uyarılar', en: 'Alerts' },
  help: { tr: 'Yardım', en: 'Help' },
  settings: { tr: 'Ayarlar', en: 'Settings' },
  logout: { tr: 'Çıkış Yap', en: 'Sign out' },
  roleSuperAdmin: { tr: 'Süper Admin', en: 'Super Admin' },
  roleOrgAdmin: { tr: 'Organizasyon Admin', en: 'Organization Admin' },
  roleUser: { tr: 'Kullanıcı', en: 'User' },
  openMenu: { tr: 'Menüyü Aç', en: 'Open menu' },
  closeMenu: { tr: 'Menüyü Kapat', en: 'Close menu' },
};

// On phones the sidebar is an off-canvas drawer (mobileOpen/onMobileClose) and
// the desktop "collapsed" rail doesn't exist — always render the full menu.
function Sidebar({ collapsed: desktopCollapsed, setCollapsed, mobileOpen = false, onMobileClose = () => {} }) {
  const isMobile = useIsMobile();
  const collapsed = desktopCollapsed && !isMobile;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useT(messages);
  const logo = useBrandLogo('white');

  const handleLogout = () => {
    onMobileClose();
    logout();
    navigate('/login');
  };

  // A super_admin sees the platform panel. One that also belongs to an
  // organization can switch to that organization's panel: /admin/* pages are
  // the platform panel, everything else is the organization panel (the API
  // client tells the backend which one — see config/api.js).
  const canSwitchPanel = user?.role === 'super_admin' && !!user?.organization;
  const inPlatformPanel = user?.role === 'super_admin' &&
    (!user?.organization || location.pathname.startsWith('/admin'));

  const navItems = inPlatformPanel
    ? [
        { key: 'adminPanel', path: '/admin/dashboard' },
        { key: 'organizations', path: '/admin/organizations' },
        { key: 'users', path: '/admin/users' },
        { key: 'licenses', path: '/admin/licenses' },
        { key: 'demoRequests', path: '/admin/demo-requests' },
        { key: 'auditLog', path: '/admin/audit-log' },
        { key: 'alerts', path: '/admin/notifications' }
      ]
    : user?.role === 'org_user'
    ? [
        { key: 'mySignature', path: '/imzam' },
        { key: 'help', path: '/help' }
      ]
    : [
        { key: 'dashboard', path: '/dashboard' },
        { key: 'mySignature', path: '/imzam' },
        { key: 'staff', path: '/users' },
        { key: 'groups', path: '/groups' },
        { key: 'signatures', path: '/signatures' },
        { key: 'disclaimers', path: '/disclaimers' },
        { key: 'campaigns', path: '/campaigns' },
        { key: 'brandAssets', path: '/brand-assets' },
        { key: 'surveys', path: '/surveys' },
        { key: 'deliveryLog', path: '/delivery-log' },
        { key: 'alerts', path: '/alerts' },
        { key: 'help', path: '/help' }
      ];

  const footerNavItems = [
    ...(canSwitchPanel
      ? [inPlatformPanel
          ? { key: 'orgPanel', onClick: () => { onMobileClose(); navigate('/dashboard'); } }
          : { key: 'superAdminPanel', onClick: () => { onMobileClose(); navigate('/admin/dashboard'); } }]
      : []),
    { key: 'settings', path: '/settings', onClick: () => { onMobileClose(); navigate('/settings'); } },
    { key: 'logout', onClick: handleLogout }
  ];

  const getRoleLabel = (role) => {
    switch(role) {
      case 'super_admin': return t('roleSuperAdmin');
      case 'org_admin': return t('roleOrgAdmin');
      default: return t('roleUser');
    }
  };

  return (
    <div className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-logovetogglebutton">
        {!collapsed && <img src={logo.src} alt={logo.alt} className="sidebar-logo" />}
        <button
          className="sidebar-toggle-btn"
          onClick={() => (isMobile ? onMobileClose() : setCollapsed(c => !c))}
          aria-label={collapsed ? t('openMenu') : t('closeMenu')}
          type="button"
        >
          <SidebarIcon className={`sidebar-toggle-icon${collapsed ? ' collapsed' : ''}`} />
        </button>
      </div>
      {!collapsed && (
        <>
          <ul className="sidebar-menu">
            {navItems.map(item => (
              <li
                key={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                <Link to={item.path} onClick={onMobileClose}>
                  {icons[item.key]}<span className="sidebar-label">{t(item.key)}</span>
                </Link>
              </li>
            ))}
          </ul>

          {/* User Info & Actions Section */}
          <div className="sidebar-footer">
            <div className="sidebar-divider"></div>
            
            {/* User Info */}
            <div className="sidebar-user-info">
              <div className="sidebar-user-avatar">{getInitials(user)}</div>
              <div className="sidebar-user-details">
                <div className="sidebar-user-name">
                  {user?.first_name || user?.email?.split('@')[0] || t('roleUser')}
                </div>
                <div className="sidebar-user-role">
                  {getRoleLabel(user?.role)}
                </div>
              </div>
            </div>
            <div className="sidebar-lang-row">
              <LanguageSwitcher />
            </div>

            {/* Action Buttons */}
            <ul className="sidebar-menu">
              {footerNavItems.map(item => (
                <li 
                  key={item.key}
                  className={item.path && location.pathname.startsWith(item.path.split('/').slice(0, 2).join('/')) ? 'active' : ''}
                >
                  <a onClick={item.onClick} title={t(item.key)}>
                    {icons[item.key]}
                    <span className="sidebar-label">{t(item.key)}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default Sidebar;
